import { BaseUrl } from '../consts';
import { loginRequest } from '../helpers/authConfig';
import { msalInstance } from '../helpers/authHelper';
import { delRequest, getRequest, postRequest, putRequest } from '../utilities/rest';

export type TUserMigrationStatus = 'SSO' | 'VCP' | 'EXT' | 'DIS' | 'INT';

export interface ISecurityUser {
    userPk?: string | number;
    userId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    migrationStatus?: TUserMigrationStatus;
    existsInAzure?: boolean;
    vendorDesc?: string;
    userGroup?: {
        groupId?: number;
        vendorId?: string;
    };
}

export interface ICreateSecurityUserRequest {
    email: string;
    firstName?: string;
    lastName?: string;
    vendorId?: string;
    groupId: number;
    initialPassword?: string;
    forceChangePasswordNextSignIn: boolean;
}

export interface IUpdateSecurityUserRequest {
    userId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    vendorId?: string;
    groupId: number;
    resetPassword: boolean;
    initialPassword?: string;
    forceChangePasswordNextSignIn: boolean;
}

const getUserKey = (user: ISecurityUser) => String(user.userPk ?? user.userId ?? user.email ?? '');

class SecurityService {
    private users: ISecurityUser[] = [];

    private getAuthHead = async (): Promise<
        | {
              authorization: string;
              pragma: string;
              cacheControl: string;
          }
        | any
    > => {
        const accounts = msalInstance.getAllAccounts();

        const res = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
        });

        return {
            authorization: `Bearer ${res.accessToken}`,
            pragma: 'no-cache',
            'cache-control': 'no-cache',
        };
    };

    getUsers = async (email?: string): Promise<ISecurityUser[]> => {
        const query = email ? `?email=${encodeURIComponent(email)}` : '';
        const users = await getRequest<ISecurityUser[]>(`${BaseUrl}security/user${query}`, {
            headers: await this.getAuthHead(),
        });

        if (!email) {
            this.users = users;
        }

        return users;
    };

    getCachedUser = (userPk: string): ISecurityUser | undefined => {
        return this.users.find((user) => getUserKey(user) === userPk);
    };

    getVendorName = async (vendorId: string): Promise<string | undefined> => {
        const result = await getRequest<{ vendorName?: string }>(
            `${BaseUrl}security/user/vendor-name/${encodeURIComponent(vendorId)}`,
            { headers: await this.getAuthHead() },
        );

        return result.vendorName;
    };

    deleteUser = async (userPk: string): Promise<void> => {
        await delRequest<void, undefined>(
            `${BaseUrl}security/user/${encodeURIComponent(userPk)}`,
            undefined,
            {
                headers: await this.getAuthHead(),
            },
        );
    };

    createUser = async (request: ICreateSecurityUserRequest): Promise<ISecurityUser> => {
        return postRequest<ICreateSecurityUserRequest, ISecurityUser>(
            `${BaseUrl}security/user`,
            request,
            {
                headers: await this.getAuthHead(),
            },
        );
    };

    updateUser = async (request: IUpdateSecurityUserRequest): Promise<void> => {
        await putRequest<IUpdateSecurityUserRequest, void>(`${BaseUrl}security/user`, request, {
            headers: await this.getAuthHead(),
        });
    };

    enableUser = async (userPk: string): Promise<void> => {
        await putRequest<undefined, void>(
            `${BaseUrl}security/user/${encodeURIComponent(userPk)}/enable`,
            undefined,
            {
                headers: await this.getAuthHead(),
            },
        );
    };

    disableUser = async (userPk: string): Promise<void> => {
        await putRequest<undefined, void>(
            `${BaseUrl}security/user/${encodeURIComponent(userPk)}/disable`,
            undefined,
            {
                headers: await this.getAuthHead(),
            },
        );
    };
}

export const securityService = new SecurityService();
